import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
  Paper,
  Chip,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Rating
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import {
  LocationOn,
  Phone,
  Email,
  AccessTime,
  ArrowBack
} from '@mui/icons-material';
import {
  useSalonsRetrieveQuery,
  useTeamsTeamsListQuery
} from '../../../services/GLOWClient';
import { getLocalizedField } from '../../../utils/localizedFields';
import { useSalonPublicTheme } from '../../../context/SalonPublicThemeContext';
import { useTranslation } from 'react-i18next';
import { BASE_URL } from '../../../config';
import { TitleScreen } from '../../../SystemDesign/styles';
import ClientServices from './ClientServices';
import ReviewModal from '../reviews/ReviewModal';
import ReviewService from '../../../services/review.service';
import Button from '../../common/salonPublicAppearance/SalonPublicButton';
import SalonPublicHeader from '../../common/salonPublicAppearance/SalonPublicHeader';
import SalonPublicHero from '../../common/salonPublicAppearance/SalonPublicHero';
import SalonPrimaryActions from './SalonPrimaryActions';
import { useClientChat } from '../messaging/widget/ClientChatContext';

// Helper function to build image URL
const buildImageUrl = (url) => {
  if (!url) return '';

  // If URL is already absolute, return as is
  if (/^https?:\/\//i.test(url)) {
    // Check if it's a localhost URL without port and add port 8012
    if (url.match(/^http:\/\/localhost\//) && !url.match(/^http:\/\/localhost:[0-9]+\//)) {
      return url.replace('http://localhost/', 'http://localhost:8012/');
    }
    return url;
  }

  // For relative URLs that start with /media/
  if (url.startsWith('/media/')) {
    // Use port 8012 for localhost in development
    const baseUrl = window.location.hostname === 'localhost' ?
      'http://localhost:8012' :
      BASE_URL.replace(/\/$/, ''); // Remove trailing slash
    return `${baseUrl}${url}`;
  }

  // For relative URLs that start with media/ (no leading slash)
  if (url.startsWith('media/')) {
    // Use port 8012 for localhost in development
    const baseUrl = window.location.hostname === 'localhost' ?
      'http://localhost:8012' :
      BASE_URL.replace(/\/$/, ''); // Remove trailing slash
    return `${baseUrl}/${url}`;
  }

  // For other relative URLs, prepend BASE_URL
  const baseUrl = window.location.hostname === 'localhost' ?
    'http://localhost:8012' :
    BASE_URL.replace(/\/$/, ''); // Remove trailing slash
  const cleanUrl = url.startsWith('/') ? url : `/${url}`;
  return `${baseUrl}${cleanUrl}`;
};

const ClientSalon = () => {
  const { t, i18n } = useTranslation();
  const { tokens: theme } = useSalonPublicTheme();

  const { salonId } = useParams();
  const navigate = useNavigate();
  const heroBoundaryRef = useRef(null);
  const [activeTab, setActiveTab] = useState(0);
  const [searchParams] = useSearchParams();
  const [startBooking, setStartBooking] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [pendingReviewAppointment, setPendingReviewAppointment] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState(null);
  const { openForSalon, setContextSalon } = useClientChat();

  // Get service parameter from URL
  const serviceParam = searchParams.get('service');
  const rebookAppointmentParam = searchParams.get('rebookAppointment');
  const reviewAppointmentIdParam = searchParams.get('reviewAppointmentId');

  // Fetch salon details
  const { data: salon, error: salonError, isLoading: salonLoading } = useSalonsRetrieveQuery({ salonId, language: i18n.language });
  const primaryPhoto = salon?.photos && Object.keys(salon.photos).length > 0
    ? Object.values(salon.photos)[0]
    : null;
  const heroImage = typeof primaryPhoto === 'string'
    ? buildImageUrl(primaryPhoto)
    : primaryPhoto?.url ? buildImageUrl(primaryPhoto.url) : '';

  useEffect(() => {
    if (!salon?.salon_id) return undefined;
    setContextSalon(salon);
    return () => setContextSalon(null);
  }, [salon, setContextSalon]);

  const getTranslatedField = (item, field) => {
    return getLocalizedField(item, field, i18n.language);
  };

  // Preload existing reviews for this salon
  useEffect(() => {
    const loadReviews = async () => {
      setReviewsLoading(true);
      setReviewsError(null);
      try {
        const data = await ReviewService.getSalonReviews(salonId);
        // Support both plain array and paginated response
        const items = Array.isArray(data) ? data : data?.results || [];
        setReviews(items);
      } catch (error) {
        console.error('Error loading salon reviews:', error);
        setReviewsError("Impossible de charger les avis pour le moment.");
      } finally {
        setReviewsLoading(false);
      }
    };

    if (salonId) {
      loadReviews();
    }
  }, [salonId]);

  // Preload a pending review appointment for this salon (if any)
  useEffect(() => {
    const loadPendingReview = async () => {
      try {
        const pending = await ReviewService.getPendingReviews(salonId);
        if (pending && pending.length > 0) {
          // For now, just take the most recent pending appointment
          const match = reviewAppointmentIdParam
            ? pending.find((p) => String(p.appointment_id) === String(reviewAppointmentIdParam))
            : pending[0];
          const selected = match || pending[0];
          setPendingReviewAppointment(selected);

          if (reviewAppointmentIdParam && selected) {
            setReviewModalOpen(true);
            navigate(`/client/salon/${salonId}`, { replace: true });
          }
        } else {
          setPendingReviewAppointment(null);
        }
      } catch (error) {
        // If user is not authenticated or any other error occurs, we silently ignore
        console.error('Error loading pending reviews for salon detail:', error);
        setPendingReviewAppointment(null);
      }
    };

    if (salonId) {
      loadPendingReview();
    }
  }, [salonId, reviewAppointmentIdParam, navigate]);

  const averageRating =
    reviews && reviews.length > 0
      ? reviews.reduce((sum, review) => sum + (review.rating || 0), 0) / reviews.length
      : null;

  const handleOpenReviewModal = () => {
    setReviewModalOpen(true);
  };

  const handleCloseReviewModal = () => {
    setReviewModalOpen(false);
  };

  const handleReviewSuccess = () => {
    // After a successful review, hide the modal and clear the pending appointment
    setReviewModalOpen(false);
    setPendingReviewAppointment(null);
  };

  // Automatically switch to Services tab when service parameter or rebooking parameter is present
  useEffect(() => {
    if ((serviceParam && serviceParam.trim() !== '') || rebookAppointmentParam) {
      setActiveTab(1); // Switch to Services tab (index 1)
    }
  }, [serviceParam, rebookAppointmentParam]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Format opening hours
  const formatOpeningHours = (openingHours) => {
    if (!openingHours) return 'Information non disponible';

    const daysOfWeek = [
      'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'
    ];

    // Get current day and time
    const now = new Date();
    const currentDayIndex = now.getDay(); // 0 is Sunday, 1 is Monday, etc.
    const currentDay = daysOfWeek[currentDayIndex === 0 ? 6 : currentDayIndex - 1]; // Adjust for our array (starts with Monday)
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    // Check if salon is currently open
    const isOpen = () => {
      const todayData = openingHours[currentDay];
      if (!todayData || todayData.ouverture === 'fermé') return false;

      // Parse opening and closing hours
      const [openHour, openMinute] = todayData.ouverture.split(':').map(Number);
      const [closeHour, closeMinute] = todayData.fermeture.split(':').map(Number);

      // Convert to minutes for easier comparison
      const currentTimeInMinutes = currentHour * 60 + currentMinute;
      const openTimeInMinutes = openHour * 60 + openMinute;
      const closeTimeInMinutes = closeHour * 60 + closeMinute;

      return currentTimeInMinutes >= openTimeInMinutes && currentTimeInMinutes < closeTimeInMinutes;
    };

    // Get status message
    const getStatusMessage = () => {
      if (!isOpen()) return 'Fermé';

      const todayData = openingHours[currentDay];
      return `Ouvert jusqu'à ${todayData.fermeture}`;
    };

    // Status message at the top
    const statusMessage = getStatusMessage();

    return (
      <Box>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 2,
          p: 1,
          borderRadius: theme.borderRadius.md,
          bgcolor: isOpen() ? alpha(theme.colors.success[500], 0.1) : alpha(theme.colors.error[500], 0.1)
        }}>
          <AccessTime sx={{
            mr: 1,
            color: isOpen() ? theme.colors.success[500] : theme.colors.error[500],
            fontSize: '1.2rem'
          }} />
          <Typography variant="subtitle1" sx={{
            fontWeight: 'medium',
            color: isOpen() ? theme.colors.success[500] : theme.colors.error[500]
          }}>
            {statusMessage}
          </Typography>
        </Box>

        <List dense sx={{ mt: 1 }}>
          {daysOfWeek.map(day => {
            const dayData = openingHours[day];
            const isCurrentDay = day === currentDay;

            return (
              <ListItem
                key={day}
                disableGutters
                sx={{
                  py: 0.5,
                  bgcolor: isCurrentDay ? 'rgba(0, 0, 0, 0.03)' : 'transparent',
                  borderRadius: theme.borderRadius.sm
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    bgcolor: theme.colors.success[500],
                    mr: 1,
                    display: 'inline-block'
                  }}
                />
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    fontWeight: isCurrentDay ? 700 : 400,
                    width: '100px',
                    display: 'inline-block'
                  }}
                >
                  {day.charAt(0).toUpperCase() + day.slice(1)}
                </Typography>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ color: theme.colors.text.secondary }}
                >
                  {dayData && dayData.ouverture !== 'fermé' ?
                    `${dayData.ouverture} - ${dayData.fermeture}` : 'Fermé'}
                </Typography>
              </ListItem>
            );
          })}
        </List>
      </Box>
    );
  };

  if (salonLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (salonError) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          Erreur lors du chargement des détails du salon: {salonError.message || 'Erreur inconnue'}
        </Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/client/salons')}
          sx={{ mt: 2 }}
        >
          Retour à la liste des salons
        </Button>
      </Box>
    );
  }

  return (
    <Container
      maxWidth="xl"
      style={{
        backgroundColor: theme.salon.background,
        fontFamily: theme.typography.fontFamily.sans.join(', '),
      }}
    >
      <SalonPublicHeader
        brand={(
          <Typography sx={{ fontWeight: 800 }}>
            {getTranslatedField(salon, 'name')}
          </Typography>
        )}
        navigation={(
          <Box component="nav" aria-label={t('client_salon.navigation')}>
            <Button
              tone="secondary"
              startIcon={<ArrowBack />}
              onClick={() => navigate('/client/salons')}
            >
              {t('actions.back')}
            </Button>
          </Box>
        )}
        heroBoundaryRef={heroBoundaryRef}
        hasHeroMedia={Boolean(heroImage)}
      />

      <SalonPublicHero
        ref={heroBoundaryRef}
        media={heroImage ? <Box component="img" src={heroImage} alt="" /> : null}
        sx={{
          minHeight: { xs: '110px', sm: '140px', md: '170px' },
          borderRadius: 4,
          mb: 3,
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            minHeight: 'inherit',
            px: { xs: 2.5, sm: 4 },
            py: { xs: 2.5, sm: 3.5 },
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            gap: { xs: 2.5, md: 4 },
            color: 'white',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 900,
                textShadow: '0 2px 10px rgba(0,0,0,0.35)',
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' },
                lineHeight: 1.1,
              }}
            >
              {getTranslatedField(salon, 'name')}
            </Typography>

            {getTranslatedField(salon, 'description') && (
              <Typography
                variant="body2"
                sx={{
                  color: 'white',
                  mt: 1,
                  maxWidth: { xs: '100%', md: '85%' },
                  lineHeight: 1.6,
                  textShadow: '0 2px 8px rgba(0,0,0,0.45)',
                  display: '-webkit-box',
                  WebkitLineClamp: { xs: 2, sm: 3 },
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {getTranslatedField(salon, 'description')}
              </Typography>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <LocationOn sx={{ fontSize: 18, color: 'rgba(255,255,255,0.85)' }} />
              <Typography
                variant="subtitle2"
                sx={{
                  opacity: 0.9,
                  fontWeight: 600,
                  textShadow: '0 2px 8px rgba(0,0,0,0.25)',
                }}
              >
                {salon?.formatted_address || t('client_salon.address_not_specified')}
              </Typography>
            </Box>
          </Box>

          <SalonPrimaryActions
            clientChatEnabled={salon?.client_chat_enabled}
            onBook={() => {
              setStartBooking(true);
              setActiveTab(1);
            }}
            onContact={() => openForSalon(salon)}
          />
        </Box>
      </SalonPublicHero>

      <Paper sx={{ mb: 4 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          textColor="primary"
          indicatorColor="primary"
          aria-label="salon tabs"
          sx={{
            mb: 3,
            '& .MuiTab-root': {
              fontWeight: theme.typography.fontWeight.medium,
              fontSize: '0.9rem',
              textTransform: 'none'
            }
          }}
        >
          <Tab
            label="Informations"
            icon={<LocationOn fontSize="small" />}
            iconPosition="start"
          />
          <Tab
            label="Services"
            icon={<Chip
              size="small"
              sx={{ height: 18, width: 18, fontSize: '0.7rem' }}
            />}
            iconPosition="start"
          />
          {/* <Tab 
            label="Rendez-vous" 
            icon={<CalendarMonth fontSize="small" />} 
            iconPosition="start" 
          /> */}
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <div role="tabpanel" hidden={activeTab !== 0}>
        {activeTab === 0 && (
          <Grid container spacing={4}>
            {/* Salon Image */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardMedia
                  component="img"
                  height="400"
                  image={salon?.photos && Object.keys(salon.photos).length > 0
                    ? (() => {
                      const photoValue = Object.values(salon.photos)[0];
                      // Check if photoValue is a string
                      if (typeof photoValue === 'string') {
                        return buildImageUrl(photoValue);
                      }
                      // Check if photoValue is an object with a url property
                      else if (photoValue && typeof photoValue === 'object' && photoValue.url) {
                        return buildImageUrl(photoValue.url);
                      }
                      // Fallback
                      return `https://via.placeholder.com/600x400?text=${encodeURIComponent(getTranslatedField(salon, 'name') || 'Salon')}`;
                    })()
                    : `https://via.placeholder.com/600x400?text=${encodeURIComponent(getTranslatedField(salon, 'name') || 'Salon')}`}
                  alt={getTranslatedField(salon, 'name')}
                  sx={{ objectFit: 'cover' }}
                />
              </Card>
            </Grid>

            {/* Salon Details */}
            <Grid item xs={12} md={6}>
              <Card sx={{
                height: '100%',
                boxShadow: theme.boxShadow.md,
                borderRadius: theme.borderRadius.lg,
                overflow: 'hidden'
              }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom sx={{
                    fontWeight: theme.typography.fontWeight.bold,
                    color: theme.colors.primary[600],
                    fontFamily: theme.typography.fontFamily.sans.join(', ')
                  }}>
                    {getTranslatedField(salon, 'name')}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  {getTranslatedField(salon, 'description') && (
                    <Typography variant="body1" paragraph>
                      {getTranslatedField(salon, 'description')}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <LocationOn sx={{ mr: 1, color: theme.colors.primary[500] }} />
                    <Typography variant="body1" sx={{ color: theme.colors.text.secondary }}>
                      {salon?.formatted_address || t('client_salon.address_not_specified')}
                    </Typography>
                  </Box>

                  {salon?.phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Phone sx={{ mr: 1, color: theme.colors.primary[500] }} />
                      <Typography variant="body1" sx={{ color: theme.colors.text.secondary }}>
                        {salon.phone}
                      </Typography>
                    </Box>
                  )}

                  {salon?.email && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Email sx={{ mr: 1, color: theme.colors.primary[500] }} />
                      <Typography variant="body1" sx={{ color: theme.colors.text.secondary }}>
                        {salon.email}
                      </Typography>
                    </Box>
                  )}

                  {/* Review action remains in the Informations card. */}
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleOpenReviewModal}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      {pendingReviewAppointment ? t('client_salon.review_pending') : t('client_salon.review_salon')}
                    </Button>
                  </Box>

                  <Box sx={{ mt: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <AccessTime sx={{ mr: 1, color: theme.colors.primary[500] }} />
                      <Typography variant="h6" sx={{
                        color: theme.colors.primary[700],
                        fontWeight: theme.typography.fontWeight.semibold
                      }}>
                        {t('client_salon.opening_hours')}
                      </Typography>
                    </Box>
                    {formatOpeningHours(salon?.opening_hours)}
                  </Box>

                  {/* Existing reviews section */}
                  <Box sx={{ mt: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: theme.colors.primary[700],
                        fontWeight: theme.typography.fontWeight.semibold,
                        mb: 1
                      }}
                    >
                      Avis des clients
                    </Typography>

                    {reviewsLoading && (
                      <Typography variant="body2" color="text.secondary">
                        {t('client_salon.loading_reviews')}
                      </Typography>
                    )}

                    {reviewsError && (
                      <Typography variant="body2" color="error">
                        {reviewsError}
                      </Typography>
                    )}

                    {!reviewsLoading && !reviewsError && reviews && reviews.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Aucun avis pour le moment.
                      </Typography>
                    )}

                    {!reviewsLoading && !reviewsError && reviews && reviews.length > 0 && (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Rating
                            name="salon-rating"
                            value={averageRating}
                            precision={0.5}
                            readOnly
                            size="small"
                          />
                          <Typography
                            variant="body2"
                            sx={{ ml: 1, color: theme.colors.text.secondary }}
                          >
                            {averageRating.toFixed(1)} / 5 · {reviews.length} avis
                          </Typography>
                        </Box>

                        <List dense sx={{ p: 0 }}>
                          {reviews.slice(0, 3).map((review) => (
                            <ListItem
                              key={review.review_id}
                              alignItems="flex-start"
                              disableGutters
                              sx={{ pb: 1.5 }}
                            >
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="subtitle2">
                                      {review.client_name || 'Client'}
                                    </Typography>
                                    <Rating
                                      value={review.rating}
                                      readOnly
                                      size="small"
                                    />
                                  </Box>
                                }
                                secondary={
                                  <>
                                    {review.comment && (
                                      <Typography
                                        variant="body2"
                                        sx={{ color: theme.colors.text.secondary }}
                                      >
                                        {review.comment}
                                      </Typography>
                                    )}
                                    {review.created_at && (
                                      <Typography
                                        variant="caption"
                                        sx={{ color: theme.colors.grey[600] }}
                                      >
                                        {new Date(review.created_at).toLocaleDateString('fr-FR')}
                                      </Typography>
                                    )}
                                  </>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      </>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </div>
      <div role="tabpanel" hidden={activeTab !== 1}>
        {activeTab === 1 && (
          <ClientServices
            salonId={salonId}
            startWithTypeSelection={startBooking}
            onResetStartBooking={() => {
              setStartBooking(false);
              setActiveTab(0);
            }}
          />
        )}
      </div>

      {/* Review modal for this salon */}
      <ReviewModal
        open={reviewModalOpen}
        onClose={handleCloseReviewModal}
        appointment={pendingReviewAppointment}
        salonId={salonId}
        salonName={getTranslatedField(salon, 'name')}
        onSuccess={handleReviewSuccess}
      />
    </Container>
  );
};

export default ClientSalon;
